import React from "react";
import {
  Screwdriver,
  ShoppingCart,
  Phone,
  Wrench,
  Notebook,
  CurrencyEur,
  Package,
  GridFour,
  Check,
  ListChecks,
} from "@phosphor-icons/react";
import { TABS, TYPE_BY_VALUE } from "./constants";

const ICON_MAP = {
  screwdriver: Screwdriver,
  cart: ShoppingCart,
  phone: Phone,
  wrench: Wrench,
  notebook: Notebook,
  euro: CurrencyEur,
  package: Package,
};

const SPECIAL_ICON = {
  all: GridFour,
  needs_payment: CurrencyEur,
  completed: Check,
};

export default function Tabs({ activeTabs, onChange, notes }) {
  const counts = React.useMemo(() => {
    const c = { all: 0, completed: 0, needs_payment: 0 };
    notes.forEach((n) => {
      if (n.completed) {
        c.completed = (c.completed || 0) + 1;
      } else {
        c.all = (c.all || 0) + 1;
        c[n.type] = (c[n.type] || 0) + 1;
        if (n.needs_payment) c.needs_payment = (c.needs_payment || 0) + 1;
      }
    });
    return c;
  }, [notes]);

  const toggle = (value) => {
    const set = new Set(activeTabs);
    // "completed" is the table-view: keep it exclusive for sanity.
    if (value === "completed") {
      if (set.has("completed") && set.size === 1) return; // can't deselect last
      onChange(new Set(["completed"]));
      return;
    }
    // If currently viewing "completed", any other click switches OFF table view
    if (set.has("completed")) {
      onChange(new Set([value]));
      return;
    }
    // Toggle
    if (set.has(value)) {
      set.delete(value);
      if (set.size === 0) set.add("all"); // never empty — fall back to ΟΛΑ
    } else {
      set.add(value);
    }
    onChange(set);
  };

  return (
    <div className="px-6 pb-2 overflow-x-auto no-scrollbar" data-testid="tabs-container">
      <div className="flex items-center gap-1.5 min-w-max">
        {TABS.map((t) => {
          const meta = TYPE_BY_VALUE[t.value];
          const active = activeTabs.has(t.value);
          const count = counts[t.value] || 0;
          const SpecialIcon = SPECIAL_ICON[t.value];
          const TypeIcon = meta ? ICON_MAP[meta.icon] : null;
          const Icon = TypeIcon || SpecialIcon || ListChecks;

          let bgColor = null;
          if (active) {
            bgColor = "#1A1A19"; // ink
          } else if (meta) {
            bgColor = meta.color;
          } else if (t.value === "needs_payment") {
            bgColor = "#fcd34d";
          } else if (t.value === "completed") {
            bgColor = "#d1fae5";
          } else {
            bgColor = "#e5e7eb";
          }

          const textColor = active ? "#fff" : "#1A1A19";

          return (
            <button
              key={t.value}
              onClick={() => toggle(t.value)}
              data-testid={`tab-${t.value}`}
              style={{ backgroundColor: bgColor, color: textColor }}
              className={`text-[11px] uppercase tracking-[0.1em] font-bold px-3 py-2 rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap border ${
                active
                  ? "border-ink shadow-md"
                  : "border-black/10 hover:border-ink/40 hover:scale-105"
              }`}
            >
              <span
                className={`w-5 h-5 rounded flex items-center justify-center ${
                  active ? "bg-white/15" : "bg-white/40"
                }`}
              >
                <Icon size={12} weight="bold" />
              </span>
              {t.label}
              {count > 0 && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    active ? "bg-white/20" : "bg-black/15"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
